import React, { useState, useEffect } from "react";
import { Input, Button, message, Select, DatePicker } from "antd";
import dayjs from "dayjs";
import ModalWrapper from "../../Modal";
import { postRequest, getRequest, putRequest } from "../../service/index";

const EditRecruitmentModal = ({ open, onRequestClose, editingData }) => {
  const [formData, setFormData] = useState({
    interviewBy: "",
    date: null,
    remark: "",
    approvedBy: "",
    status: "failed"
  });
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // Fetch employees for dropdowns and populate form data
  useEffect(() => {
    if (open) {
      fetchEmployees();
      populateFormData();
    }
  }, [open, editingData]);

  // Populate form data with existing data
  const populateFormData = () => {
    if (!editingData || !editingData.fullData) return;

    const fullData = editingData.fullData;
    const stage = getCurrentStage();
    
    switch (stage) {
      case 'interview_schedule':
        if (fullData.firstInterview) {
          setFormData({
            interviewBy: fullData.firstInterview.interviewBy?._id || "",
            date: fullData.firstInterview.date ? dayjs(fullData.firstInterview.date) : null,
            remark: fullData.firstInterview.remark || "",
            approvedBy: "",
            status: "failed"
          });
        }
        break;
        
      case 'interview_conduct':
        if (fullData.secondInterview) {
          setFormData({
            interviewBy: fullData.secondInterview.interviewBy?._id || "",
            date: fullData.secondInterview.date ? dayjs(fullData.secondInterview.date) : null,
            remark: fullData.secondInterview.remark || "",
            approvedBy: "",
            status: "failed"
          });
        }
        break;
        
      case 'second_interview':
        if (fullData.shortlist) {
          setFormData({
            interviewBy: "",
            date: fullData.shortlist.date ? dayjs(fullData.shortlist.date) : null,
            remark: fullData.shortlist.remark || "",
            approvedBy: fullData.shortlist.approvedBy?._id || "",
            status: fullData.status || "failed"
          });
        }
        break;
        
      default:
        // Reset form data
        setFormData({
          interviewBy: "",
          date: null,
          remark: "",
          approvedBy: "",
          status: "failed"
        });
    }
  };

  const fetchEmployees = async () => {
    try {
      setEmployeeLoading(true);
      const response = await getRequest('/api/companies/reportto');
      if (response && response.employees) {
        setEmployees(response.employees);
      }
      
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setEmployeeLoading(false);
    }
  };

  // Get the current stage from editingData
  const getCurrentStage = () => {
    if (!editingData) return null;
    
    switch (editingData.stage) {
      case 'interview_schedule':
        return 'interview_schedule';
      case 'interview_conduct':
        return 'interview_conduct';
      case 'second_interview':
        return 'second_interview';
      default:
        return null;
    }
  };

  // Get API endpoint based on stage
  const getApiEndpoint = () => {
    const stage = getCurrentStage();
    if (!stage || !editingData?.id) return null;
    
    switch (stage) {
      case 'interview_schedule':
        return `/api/onboarding/${editingData.id}/first-interview`;
      case 'interview_conduct':
        return `/api/onboarding/${editingData.id}/second-interview`;
      case 'second_interview':
        return `/api/onboarding/${editingData.id}/shortlist`;
      default:
        return null;
    }
  };

  // Get modal title based on stage
  const getModalTitle = () => {
    const stage = getCurrentStage();
    switch (stage) {
      case 'interview_schedule':
        return "First Interview Details";
      case 'interview_conduct':
        return "Second Interview Details";
      case 'second_interview':
        return "Shortlist Results";
      default:
        return "Edit Recruitment";
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    const currentStage = getCurrentStage();
    const apiEndpoint = getApiEndpoint();
    
    if (!apiEndpoint) {
      message.error("Invalid stage for editing");
      return;
    }

    let payload = {};
    let successMessage = "";
    let newStatus = "";

    switch (currentStage) {
      case 'interview_schedule':
        // First interview form
        if (!formData.interviewBy || !formData.date) {
          message.error("Please fill in Interview By and Date");
          return;
        }
        payload = {
          interviewBy: formData.interviewBy,
          date: formData.date ? formData.date.toISOString() : null,
          remark: formData.remark || ""
        };
        newStatus = "first interview";
        successMessage = "First interview details updated successfully";
        break;

      case 'interview_conduct':
        // Second interview form
        if (!formData.interviewBy || !formData.date) {
          message.error("Please fill in Interview By and Date");
          return;
        }
        payload = {
          interviewBy: formData.interviewBy,
          date: formData.date ? formData.date.toISOString() : null,
          remark: formData.remark || ""
        };
        newStatus = "second interview";
        successMessage = "Second interview details updated successfully";
        break;

      case 'second_interview':
        // Shortlist form
        if (!formData.approvedBy || !formData.date || !formData.status) {
          message.error("Please fill in Approved By, Date and Status");
          return;
        }
        payload = {
          approvedBy: formData.approvedBy,
          date: formData.date ? formData.date.toISOString() : null,
          remark: formData.remark || "",
          status: formData.status
        };
        newStatus = formData.status; // Use the selected status (failed/success)
        successMessage = "Shortlist details updated successfully";
        break;

      default:
        message.error("Invalid stage for editing");
        return;
    }

    try {
      setLoading(true);
      
      // First, update the interview/shortlist details
      const response = await postRequest(apiEndpoint, payload);
      if (response) {
        // Then, update the main status
        await putRequest(`/api/onboarding/${editingData.id}`, {
          status: newStatus
        });
        
        message.success(successMessage);
        onRequestClose();
        handleCancel();
      }
    } catch (error) {
      message.error("Failed to update details");
      console.error("Error updating details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      interviewBy: "",
      date: null,
      remark: "",
      approvedBy: "",
      status: "failed"
    });
    onRequestClose();
  };

  const renderFormFields = () => {
    const stage = getCurrentStage();
    
    switch (stage) {
      case 'interview_schedule':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-blue-800">First Interview Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Interview By*</label>
                <Select
                  placeholder="Select interviewer"
                  value={formData.interviewBy}
                  onChange={(value) => handleInputChange("interviewBy", value)}
                  className="w-full"
                  loading={employeeLoading}
                  options={employees.map(emp => ({
                    value: emp._id,
                    label: emp.name
                  }))}
                />
              </div>
              <div>
                <label className="block mb-1">Date*</label>
                <DatePicker
                  placeholder="Select interview date"
                  value={formData.date}
                  onChange={(date) => handleInputChange("date", date)}
                  className="w-full"
                  showTime
                  format="YYYY-MM-DD HH:mm"
                />
              </div>
            </div>
            <div>
              <label className="block mb-1">Remark</label>
              <Input.TextArea
                placeholder="Enter interview remarks"
                value={formData.remark}
                onChange={(e) => handleInputChange("remark", e.target.value)}
                rows={3}
              />
            </div>
          </div>
        );

      case 'interview_conduct':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-green-800">Second Interview Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Interview By*</label>
                <Select
                  placeholder="Select interviewer"
                  value={formData.interviewBy}
                  onChange={(value) => handleInputChange("interviewBy", value)}
                  className="w-full"
                  loading={employeeLoading}
                  options={employees.map(emp => ({
                    value: emp._id,
                    label: emp.name
                  }))}
                />
              </div>
              <div>
                <label className="block mb-1">Date*</label>
                <DatePicker
                  placeholder="Select interview date"
                  value={formData.date}
                  onChange={(date) => handleInputChange("date", date)}
                  className="w-full"
                  showTime
                  format="YYYY-MM-DD HH:mm"
                />
              </div>
            </div>
            <div>
              <label className="block mb-1">Remark</label>
              <Input.TextArea
                placeholder="Enter interview remarks"
                value={formData.remark}
                onChange={(e) => handleInputChange("remark", e.target.value)}
                rows={3}
              />
            </div>
          </div>
        );

      case 'second_interview':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-800">Shortlist Results</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Approved By*</label>
                <Select
                  placeholder="Select approver"
                  value={formData.approvedBy}
                  onChange={(value) => handleInputChange("approvedBy", value)}
                  className="w-full"
                  loading={employeeLoading}
                  options={employees.map(emp => ({
                    value: emp._id,
                    label: emp.name
                  }))}
                />
              </div>
              <div>
                <label className="block mb-1">Date*</label>
                <DatePicker
                  placeholder="Select approval date"
                  value={formData.date}
                  onChange={(date) => handleInputChange("date", date)}
                  className="w-full"
                  showTime
                  format="YYYY-MM-DD HH:mm"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Status*</label>
                <Select
                  placeholder="Select status"
                  value={formData.status}
                  onChange={(value) => handleInputChange("status", value)}
                  className="w-full"
                  options={[
                    { value: "failed", label: "Failed" },
                    { value: "success", label: "Success" }
                  ]}
                />
              </div>
              <div>
                <label className="block mb-1">Remark</label>
                <Input.TextArea
                  placeholder="Enter approval remarks"
                  value={formData.remark}
                  onChange={(e) => handleInputChange("remark", e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>
        );

      default:
        return <div>Invalid stage for editing</div>;
    }
  };

  if (!editingData) return null;

  return (
    <ModalWrapper title={getModalTitle()} open={open} onRequestClose={onRequestClose}>
      <div className="flex flex-col gap-4 px-2">
        {renderFormFields()}
        
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading}
            className="bg-primary hover:bg-primary/80"
          >
            Update
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default EditRecruitmentModal;
